import { useMemo, useState } from "react";
import SubHeader from "../../custom/sub-header/sub-header";
import SiteFooter from "../../custom/site-footer/site-footer";
import ReviewCard from "../../custom/review-card/review-card";
import useReviews from "../../../hooks/useReviews";
import "./reviews-page-style.scss";

//評價頁（首頁「真實回饋」區塊的「看更多」）
//
//  用 SubHeader 而不是 NavList：這是從首頁進來的下一層，要給人退路。
//  （分類頁用 NavList 是因為切分類是同一層的橫移，不是往下一層走。）
//
//  篩選列的選項是從評價自己長出來的，不是另外維護一張清單 ——
//  寫死的話，哪天某支產品還沒有評價，就會出現一個點下去空空的分頁。
//
//  排序不做：清單本來就是新到舊排好的（後端 sort createdAt: -1），
//  而「最有幫助」這種排序要有真的互動資料才算得出來，現在加等於假裝有那個功能。
const ReviewsPage = () => {
  //真實評價不足時用備援那份，兩者不混著放（見 useReviews）
  const { reviews } = useReviews();

  //null = 全部
  const [code, setCode] = useState(null);

  //有評價的產品才成為一個分頁。順序照評價出現的順序 ——
  //  也就是「最近有人評價的排前面」，比按產品表的順序更貼近這一頁在講的事。
  const tabs = useMemo(() => {
    const seen = new Map();
    reviews.forEach((r) => {
      const p = r.product;
      if (p?.code && !seen.has(p.code)) {
        //分頁上只放角色名（「白夜」），不放完整品名（「白夜・八字批命」）——
        //  一列要放四五個，完整品名會擠到要橫向捲動才看得完。
        seen.set(p.code, p.title.split("・")[0]);
      }
    });
    return [...seen].map(([value, label]) => ({ value, label }));
  }, [reviews]);

  const list = useMemo(
    () => (code ? reviews.filter((r) => r.product?.code === code) : reviews),
    [code, reviews],
  );

  //平均星等。四捨五入到小數一位 —— 進位到整數會讓 4.8 顯示成 5.0，
  //  那就等於在謊報；兩位小數又太像在報實驗數據。
  const average = useMemo(() => {
    if (reviews.length === 0) return "0.0";
    const sum = reviews.reduce((acc, r) => acc + (r.rating || 0), 0);
    return (sum / reviews.length).toFixed(1);
  }, [reviews]);

  return (
    <>
      <SubHeader title="真實回饋" />

      <main className="rvp">
        <section className="rvp__summary container">
          <p className="rvp__score">{average}</p>
          <div className="rvp__score-side">
            <p className="rvp__stars" aria-label={`平均 ${average} 顆星`}>
              <span aria-hidden="true">★★★★★</span>
            </p>
            <p className="rvp__count">共 {reviews.length} 則評價</p>
          </div>
        </section>

        {/* 篩選列：可橫向捲動，產品多了也不會擠壓（同 nav 的分類列） */}
        <div className="rvp__filter-row">
          <div className="rvp__filter no-scrollbar">
            <button
              type="button"
              className={`rvp__chip${code === null ? " is-active" : ""}`}
              onClick={() => setCode(null)}
            >
              全部
            </button>

            {tabs.map((t) => (
              <button
                key={t.value}
                type="button"
                className={`rvp__chip${code === t.value ? " is-active" : ""}`}
                onClick={() => setCode(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rvp__list container">
          {list.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      </main>

      <SiteFooter />
    </>
  );
};

export default ReviewsPage;
